// Pretty-print a worker-failure alert's `details.transcript_excerpt` — the operator
// ask from glm_fair_usage_throttle_router_blind_2026_09_02.md ("the Alerts detail
// should use the SAME pretty rendering the Fleet Workers Logs view uses").
//
// The Activity -> Alerts detail expander used to dump `transcript_excerpt` (up to
// 12,000 chars of escaped `\n` text, see server/provider_failure_alert.py) via a
// bare `<pre>{JSON.stringify(details, null, 2)}</pre>` — unreadable next to Fleet
// Workers' "Logs" action, which renders a transcript as per-turn blocks
// (TranscriptView.tsx). TranscriptView itself needs the STRUCTURED
// `TranscriptEvent[]` shape (`server/transcript_log.py::parse_transcript_events` —
// tool_use ids, separate stdout/stderr, TodoWrite/Edit diffs) that
// `transcript_excerpt` does not carry: the excerpt is the OTHER, flattened
// human-text renderer's output instead (`render_transcript_since`,
// `_render_event_line`/`_render_blocks`), e.g.
//
//   [15:04:02] 🟦 user:
//   <text>
//
//   [15:04:03] 🔧 Bash: ls -la
//      ⎿ result: <text>
//
// This module is the "shared parser" the task called for: a pure, line-based
// parser (`parseTranscriptExcerpt`) that turns that exact text format back into
// turn-shaped blocks, rendered with the SAME visual language as TranscriptView
// (timestamp chips, monospace tool results, collapsed-by-default long blocks) —
// without needing the richer structured shape TranscriptView requires. A block
// this parser can't confidently attribute (e.g. the excerpt started mid-tool-
// result because it was tail-trimmed, or a `⎿` result line with no preceding
// `🔧` header — the tail window cut the header off) still renders, just without
// a name/summary, rather than being dropped silently.

import type { CSSProperties } from "react";

export type ExcerptTextBlock = {
  kind: "user" | "assistant" | "thinking" | "system";
  ts: string;
  text: string;
};

export type ExcerptToolBlock = {
  kind: "tool";
  ts: string;
  name: string;
  summary: string;
  result: { text: string; isError: boolean } | null;
};

export type ExcerptOrphanResultBlock = {
  kind: "tool_result_orphan";
  text: string;
  isError: boolean;
};

export type ExcerptRawBlock = { kind: "raw"; text: string };

export type ExcerptBlock = ExcerptTextBlock | ExcerptToolBlock | ExcerptOrphanResultBlock | ExcerptRawBlock;

const USER_RE = /^\[(\d{2}:\d{2}:\d{2})] 🟦 user:$/;
const ASSISTANT_RE = /^\[(\d{2}:\d{2}:\d{2})] 🟢 assistant:$/;
const THINKING_RE = /^\[(\d{2}:\d{2}:\d{2})] 💭 thinking:$/;
const SYSTEM_RE = /^\[(\d{2}:\d{2}:\d{2})] ⚙ system: (.*)$/;
const TOOL_RE = /^\[(\d{2}:\d{2}:\d{2})] 🔧 (.+?): (.*)$/;
const RESULT_RE = /^ {3}⎿ (⚠ error|result): (.*)$/;
const ANY_HEADER_RE = /^\[\d{2}:\d{2}:\d{2}] (?:🟦|🟢|💭|🔧|⚙)/;

function isHeaderOrResult(line: string): boolean {
  return ANY_HEADER_RE.test(line) || RESULT_RE.test(line);
}

/** Gather lines starting at `startIdx` for a user/assistant/thinking/raw text
 * body: stops at (and consumes) the first blank line — `_render_blocks` always
 * appends one after a text block — or at the next header/result line (does not
 * consume it), whichever comes first. */
function collectTextBody(lines: string[], startIdx: number): { text: string; nextIdx: number } {
  const collected: string[] = [];
  let j = startIdx;
  while (j < lines.length) {
    const line = lines[j];
    if (line === "") {
      j++;
      break;
    }
    if (isHeaderOrResult(line)) break;
    collected.push(line);
    j++;
  }
  return { text: collected.join("\n"), nextIdx: j };
}

/** Gather a tool result's continuation lines (a multi-line stdout/tool_result
 * dump has no per-line `⎿` prefix past the first) — stops at the next header/
 * result line or EOF; unlike `collectTextBody`, a blank line is real content
 * here (the server never appends a trailing blank after a tool block). */
function collectResultContinuation(
  lines: string[],
  startIdx: number,
  firstLineText: string,
): { text: string; nextIdx: number } {
  const parts = [firstLineText];
  let j = startIdx + 1;
  while (j < lines.length && !isHeaderOrResult(lines[j])) {
    parts.push(lines[j]);
    j++;
  }
  return { text: parts.join("\n"), nextIdx: j };
}

/** Parse a `transcript_excerpt` string (render_transcript_since's flattened-text
 * format) into turn-shaped blocks. Pure + total: never throws, degrades to a
 * `raw` block for anything it can't attribute to a known line shape (a tail-
 * trimmed excerpt can start mid-block). */
export function parseTranscriptExcerpt(excerpt: string): ExcerptBlock[] {
  if (!excerpt) return [];
  const lines = excerpt.split("\n");
  const blocks: ExcerptBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line === "") {
      i++;
      continue;
    }

    const userM = line.match(USER_RE);
    const assistantM = line.match(ASSISTANT_RE);
    const thinkingM = line.match(THINKING_RE);
    const systemM = line.match(SYSTEM_RE);
    const toolM = line.match(TOOL_RE);
    const resultM = line.match(RESULT_RE);

    if (userM || assistantM || thinkingM) {
      const m = (userM ?? assistantM ?? thinkingM) as RegExpMatchArray;
      const kind = userM ? "user" : assistantM ? "assistant" : "thinking";
      const { text, nextIdx } = collectTextBody(lines, i + 1);
      blocks.push({ kind, ts: m[1], text });
      i = nextIdx;
    } else if (systemM) {
      blocks.push({ kind: "system", ts: systemM[1], text: systemM[2] });
      i++;
    } else if (toolM) {
      const ts = toolM[1];
      const name = toolM[2];
      const summary = toolM[3];
      let nextI = i + 1;
      let result: ExcerptToolBlock["result"] = null;
      if (nextI < lines.length) {
        const rm = lines[nextI].match(RESULT_RE);
        if (rm) {
          const isError = rm[1] === "⚠ error";
          const { text, nextIdx } = collectResultContinuation(lines, nextI, rm[2]);
          result = { text, isError };
          nextI = nextIdx;
        }
      }
      blocks.push({ kind: "tool", ts, name, summary, result });
      i = nextI;
    } else if (resultM) {
      // A `⎿` line with no preceding `🔧` header on the SAME rendered turn —
      // the excerpt window cut the header off (tail-trimmed at _EXCERPT_MAX_CHARS,
      // or since_offset landed mid-turn). Still render it rather than drop it.
      const isError = resultM[1] === "⚠ error";
      const { text, nextIdx } = collectResultContinuation(lines, i, resultM[2]);
      blocks.push({ kind: "tool_result_orphan", text, isError });
      i = nextIdx;
    } else {
      const { text, nextIdx } = collectTextBody(lines, i);
      if (text.trim()) blocks.push({ kind: "raw", text });
      i = nextIdx > i ? nextIdx : i + 1;
    }
  }
  return blocks;
}

const KIND_LABEL: Record<ExcerptTextBlock["kind"], string> = {
  user: "🟦 user",
  assistant: "🟢 assistant",
  thinking: "💭 thinking",
  system: "⚙ system",
};

// A collapsed-by-default threshold for long tool-result bodies — short results
// (the common case: an error message, a one-line status) stay open so an
// operator scanning several alerts in a row doesn't have to click through
// each one; a long dump (a file tail, a stack trace) starts collapsed.
const COLLAPSE_THRESHOLD_CHARS = 400;

const monoResultStyle: CSSProperties = {
  margin: "4px 0 0",
  padding: "6px 8px",
  background: "var(--bg-3)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-2)",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 320,
  overflow: "auto",
};

const errorResultStyle: CSSProperties = {
  ...monoResultStyle,
  borderColor: "var(--status-stale)",
  color: "var(--status-stale)",
  background: "var(--status-stale-bg)",
};

function TsChip({ ts }: { ts: string }) {
  return (
    <span className="mono" style={{ color: "var(--fg-2)", fontSize: 11, width: 58, flexShrink: 0 }}>
      {ts || "—"}
    </span>
  );
}

function ResultBlock({ text, isError }: { text: string; isError: boolean }) {
  const long = text.length > COLLAPSE_THRESHOLD_CHARS;
  const body = <pre style={isError ? errorResultStyle : monoResultStyle}>{text}</pre>;
  if (!long) return body;
  const preview = text.replace(/\s+/g, " ").trim().slice(0, 90);
  return (
    <details>
      <summary className="mono" style={{ fontSize: 11, color: "var(--fg-2)", cursor: "pointer" }}>
        {isError ? "⚠ error" : "result"} ({text.length} chars) — {preview}…
      </summary>
      {body}
    </details>
  );
}

function TextBlockRow({ block }: { block: ExcerptTextBlock }) {
  return (
    <div style={{ padding: "4px 0", display: "flex", gap: 8, alignItems: "flex-start" }}>
      <TsChip ts={block.ts} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: "var(--fg-2)", marginBottom: 2 }}>{KIND_LABEL[block.kind]}</div>
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12.5 }}>{block.text}</div>
      </div>
    </div>
  );
}

function ToolBlockRow({ block }: { block: ExcerptToolBlock }) {
  return (
    <div style={{ padding: "4px 0", display: "flex", gap: 8, alignItems: "flex-start" }}>
      <TsChip ts={block.ts} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5 }}>
          <span className="mono" style={{ fontWeight: 600, color: "var(--accent)" }}>
            🔧 {block.name}
          </span>
          {block.summary ? (
            <span className="mono" style={{ color: "var(--fg-2)", marginLeft: 6 }}>
              {block.summary}
            </span>
          ) : null}
        </div>
        {block.result ? (
          <ResultBlock text={block.result.text} isError={block.result.isError} />
        ) : (
          <div style={{ color: "var(--fg-2)", fontSize: 11, fontStyle: "italic" }}>no result in this excerpt</div>
        )}
      </div>
    </div>
  );
}

function OrphanResultRow({ block }: { block: ExcerptOrphanResultBlock }) {
  return (
    <div style={{ padding: "4px 0", display: "flex", gap: 8, alignItems: "flex-start" }}>
      <TsChip ts="" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: "var(--fg-2)", marginBottom: 2 }}>
          {block.isError ? "⚠ error" : "⎿ result"}
        </div>
        <ResultBlock text={block.text} isError={block.isError} />
      </div>
    </div>
  );
}

/** Renders parsed `ExcerptBlock`s in the same visual language as
 * TranscriptView.tsx's structured renderer — timestamp chips, monospace tool
 * results, collapsed-by-default long blocks — without requiring the richer
 * structured `TranscriptEvent[]` shape TranscriptView needs. */
export function TranscriptExcerptBlocks({ blocks }: { blocks: ExcerptBlock[] }) {
  if (blocks.length === 0) {
    return <div style={{ color: "var(--fg-2)", fontSize: 12, fontStyle: "italic" }}>(empty excerpt)</div>;
  }
  return (
    <div data-testid="transcript-excerpt-pretty" style={{ fontFamily: "var(--font-sans)" }}>
      {blocks.map((b, i) => {
        if (b.kind === "tool") return <ToolBlockRow key={i} block={b} />;
        if (b.kind === "tool_result_orphan") return <OrphanResultRow key={i} block={b} />;
        if (b.kind === "raw")
          return (
            <pre key={i} style={monoResultStyle}>
              {b.text}
            </pre>
          );
        return <TextBlockRow key={i} block={b} />;
      })}
    </div>
  );
}

/** Compact chips for `details.matched_signatures` — `name · category ·
 * providers` per entry, the shape `provider_failure_alert.py` writes
 * (`{"name": s.name, "category": s.category, "providers": "a,b"}`). Tolerant of
 * an unexpected shape (renders nothing rather than throwing on a malformed row). */
export interface MatchedSignatureRow {
  name: string;
  category: string;
  providers: string;
}

/** Pure shaping step for `MatchedSignatureChips` — pulled out so the "one chip
 * per matched signature, tolerant of a malformed row" contract is unit-
 * testable without evaluating JSX (this repo's dashboard tests are pure-
 * function only, see TranscriptView.test.ts). Matches the shape
 * `provider_failure_alert.py` writes: `{"name": s.name, "category": s.category,
 * "providers": ",".join(s.providers)}`. */
export function matchedSignatureRows(signatures: unknown): MatchedSignatureRow[] {
  if (!Array.isArray(signatures)) return [];
  const objRows = signatures.filter((s): s is Record<string, unknown> => typeof s === "object" && s !== null);
  return objRows.map((s) => ({
    name: typeof s.name === "string" ? s.name : "unknown_signature",
    category: typeof s.category === "string" ? s.category : "",
    providers: typeof s.providers === "string" ? s.providers : "",
  }));
}

export function MatchedSignatureChips({ signatures }: { signatures: unknown }) {
  const rows = matchedSignatureRows(signatures);
  if (rows.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
      {rows.map((s, i) => {
        const { name, category, providers } = s;
        return (
          <span
            key={i}
            className="mono"
            title={[name, category, providers].filter(Boolean).join(" · ")}
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 999,
              background: "var(--status-stale-bg)",
              color: "var(--status-stale)",
              border: "1px solid var(--status-stale)",
            }}
          >
            {name}
            {category ? ` · ${category}` : ""}
            {providers ? ` · ${providers}` : ""}
          </span>
        );
      })}
    </div>
  );
}
